import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Paper,
} from '@mui/material';
import {
  School as SchoolIcon,
  ArrowForward as ArrowForwardIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { getAvatarColor, getInitials } from '../../utils/avatar';

const ChildrenList = ({ children, loading, error, onChildSelect }) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (children.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Paper
          sx={{
            textAlign: 'center',
            py: 6,
            px: 3,
            borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.05) 100%)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }}
        >
          <PersonIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary" fontWeight={600}>
            No children found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please contact the school administrator if you believe this is an error.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 3, sm: 4 } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {children.map((child, index) => {
          const avatarColor = getAvatarColor(child.full_name);
          return (
            <Card
              key={child.id}
              onClick={() => onChildSelect(child)}
              sx={{
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,1) 100%)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.05)',
                border: '1px solid',
                borderColor: 'rgba(0,0,0,0.06)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '5px',
                  background: `linear-gradient(180deg, ${avatarColor} 0%, ${avatarColor}DD 100%)`,
                  borderRadius: '4px 0 0 4px',
                },
                '&:hover': {
                  transform: 'translateY(-6px) scale(1.01)',
                  boxShadow: `0 12px 40px ${avatarColor}25, 0 4px 12px rgba(0,0,0,0.1)`,
                  borderColor: `${avatarColor}40`,
                  '& .arrow-icon': {
                    transform: 'translateX(4px)',
                    color: avatarColor,
                  },
                  '& .avatar-container': {
                    transform: 'scale(1.05)',
                    boxShadow: `0 8px 24px ${avatarColor}40`,
                  },
                },
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                  <Box
                    className="avatar-container"
                    sx={{
                      position: 'relative',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: avatarColor,
                        width: { xs: 70, sm: 80 },
                        height: { xs: 70, sm: 80 },
                        fontSize: { xs: 28, sm: 32 },
                        fontWeight: 700,
                        color: '#ffffff',
                        boxShadow: `0 4px 16px ${avatarColor}40`,
                        border: '3px solid',
                        borderColor: `${avatarColor}20`,
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          inset: 0,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 100%)',
                          pointerEvents: 'none',
                        },
                      }}
                    >
                      {getInitials(child.full_name)}
                    </Avatar>
                  </Box>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography 
                      variant="h6" 
                      fontWeight={800}
                      sx={{
                        fontSize: { xs: '1.1rem', sm: '1.25rem' },
                        mb: 0.75,
                        color: 'text.primary',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {child.full_name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <SchoolIcon sx={{ fontSize: 18, color: 'text.secondary', opacity: 0.7 }} />
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          fontSize: { xs: '0.85rem', sm: '0.9rem' },
                          fontWeight: 600,
                        }}
                      >
                        Class: {child.class_name}
                        {child.class_section && ` - ${child.class_section}`}
                      </Typography>
                    </Box>
                    {child.roll_number && (
                      <Chip
                        label={`Roll No: ${child.roll_number}`}
                        size="small"
                        sx={{ 
                          mt: 0.5,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          bgcolor: `${avatarColor}15`,
                          color: avatarColor,
                          border: `1px solid ${avatarColor}30`,
                          height: 24,
                        }}
                      />
                    )}
                  </Box>
                  <Box
                    className="arrow-icon"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: 'rgba(0,0,0,0.04)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      flexShrink: 0,
                    }}
                  >
                    <ArrowForwardIcon 
                      sx={{ 
                        color: 'text.secondary',
                        fontSize: 20,
                        transition: 'all 0.3s ease',
                      }} 
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default ChildrenList;

